/**
 * Runtime accessor for jsPDF + jspdf-autotable.
 *
 * WHY THIS EXISTS
 * ---------------
 * Tools reach jsPDF from two very different runtimes:
 *
 *   1. The `/tools/{slug}` page, a normal Next build where `import('jspdf')`
 *      resolves against node_modules.
 *   2. An R2 tool bundle inlined into a blog post. That bundle is a standalone
 *      IIFE built by `scripts/build-tool-bundles.mjs` with `jspdf` and
 *      `jspdf-autotable` marked **external**. DynamicToolLoader shims
 *      `window.require` for externals, but it cannot shim a dynamic `import()`:
 *      esbuild leaves the bare specifier `import("jspdf")` in the output and the
 *      browser rejects it with "Failed to resolve module specifier". The PDF
 *      button then silently does nothing.
 *
 * A bare `await import('jspdf')` therefore works in (1) and is broken in (2),
 * and the breakage is invisible from reading the source — which is how it
 * survived across 26 tools. `tests/lib/r2-bundle-dependencies.test.ts` scans the
 * built bundles for the bare specifier and fails on any new one.
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 * Read the UMD global `window.jspdf`, which is the one access path that works in
 * both runtimes:
 *
 *   - In an R2 embed DynamicToolLoader has already injected the pinned jsDelivr
 *     scripts (keyed off `TOOLS_NEEDING_JSPDF`), so the global is there and this
 *     resolves immediately with no network work.
 *   - On a `/tools` page nothing injects it, so we load the same pinned scripts
 *     on demand — the mechanism `components/LazyCharts.tsx` already uses for the
 *     charts and PDFs on those pages. Without that fallback, reading
 *     `window.jspdf` alone would fix the embed and quietly break the page.
 *
 * Keep the pinned versions identical to the ones in DynamicToolLoader and
 * LazyCharts; a mismatch means an embed and a page render different PDFs.
 */

const JSPDF_CDN = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js';
const AUTOTABLE_CDN =
  'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.4/dist/jspdf.plugin.autotable.min.js';

/**
 * Type-only references: `import('jspdf')` in type position is erased by tsc and
 * esbuild, so this costs nothing at runtime and keeps every call site typed
 * exactly as it was under `await import('jspdf')`.
 */
type JsPDFConstructor = typeof import('jspdf').default;
type AutoTableFn = typeof import('jspdf-autotable').default;

export interface JsPDFRuntime {
  jsPDF: JsPDFConstructor;
  autoTable: AutoTableFn;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const globals = (): Record<string, any> => window as unknown as Record<string, any>;

/**
 * Some pinned CDN builds contain `?.` sequences that older Safari/Firefox
 * tokenise wrongly once they are re-served from a blob URL. LazyCharts applies
 * the same normalisation; keep the two in step.
 */
function normalizeCdnScript(source: string): string {
  return source.replace(/\?(\.\d)/g, '? $1');
}

async function injectScript(url: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load ${url}`);
  const objectUrl = URL.createObjectURL(
    new Blob([normalizeCdnScript(await response.text())], { type: 'application/javascript' }),
  );
  try {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      // Deliberately synchronous ordering: autotable must execute after jspdf.
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to execute ${url}`));
      script.src = objectUrl;
      document.head.appendChild(script);
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** autoTable is a plugin: it installs `doc.autoTable` and may also expose a global. */
function resolveAutoTable(): AutoTableFn {
  const call = (doc: any, options: any) => {
    const w = globals();
    const fn =
      typeof w.autoTable === 'function'
        ? w.autoTable
        : typeof w.jspdfAutoTable === 'function'
          ? w.jspdfAutoTable
          : null;
    if (fn) return fn(doc, options);
    if (typeof doc?.autoTable === 'function') return doc.autoTable(options);
    throw new Error('jspdf-autotable is not available');
  };
  return call as unknown as AutoTableFn;
}

function autoTableIsLoaded(): boolean {
  const w = globals();
  return (
    typeof w.autoTable === 'function' ||
    typeof w.jspdfAutoTable === 'function' ||
    typeof w.jspdf?.jsPDF?.API?.autoTable === 'function'
  );
}

let pending: Promise<JsPDFRuntime> | null = null;

/**
 * Resolve the jsPDF constructor and the autoTable helper, loading the pinned
 * UMD builds first if nothing has put them on `window` yet.
 *
 * Replaces `await import('jspdf')` / `await import('jspdf-autotable')`. Rejects
 * rather than resolving with a half-loaded library, so a caller that does not
 * catch surfaces a real error instead of a dead button.
 */
export function loadJsPDF(): Promise<JsPDFRuntime> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('jsPDF is only available in the browser'));
  }
  if (!pending) {
    pending = (async () => {
      if (!globals().jspdf?.jsPDF) await injectScript(JSPDF_CDN);
      const jsPDF = globals().jspdf?.jsPDF;
      if (typeof jsPDF !== 'function') throw new Error('jsPDF failed to load');
      if (!autoTableIsLoaded()) await injectScript(AUTOTABLE_CDN);
      return { jsPDF: jsPDF as JsPDFConstructor, autoTable: resolveAutoTable() };
    })();
    // A transient CDN failure must not poison every later attempt.
    pending.catch(() => {
      pending = null;
    });
  }
  return pending;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
